import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { MaterialReactTable } from 'material-react-table'
import { Send as SendIcon } from '@mui/icons-material'
import { toast } from 'react-toastify'
import './ResumenCompra.css'
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  ListItemText,
  OutlinedInput,
  Chip,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material'
import { formatNumber } from '@auteco/utils/formatsNumber'
import {
  getResumenCompra,
  crearEscenarioCompra,
  eliminarEscenarioCompra,
  calcularGuardarInventarioCompania,
  eliminarEscenarioPorModelo,
  calcularGuardarInventarioPorModelo,
  getContrasteEscenarios,
  compraEnFirme,
  actualizarFOB,
  getCongelarFechaEstado,
  congelarFecha,
  actualizarLlegadas,
} from '../../../services/motosSnopServices'

// Función para obtener nombres de meses en español
const getMesesEspanol = () => {
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ]
  
  const fechaActual = new Date()
  const mesActual = fechaActual.getMonth() // 0-11
  const mesAnterior = mesActual === 0 ? 11 : mesActual - 1
  
  return {
    mesAnterior: meses[mesAnterior],
    mesActual: meses[mesActual]
  }
}

const STORAGE_KEY_SEGMENTOS = 'resumenCompra_segmentosSeleccionados'
const STORAGE_KEY_ESTADOS = 'resumenCompra_estadosSeleccionados'
const STORAGE_KEY_MODELOS_FILTRADOS = 'resumenCompra_modelosFiltrados'
const STORAGE_KEY_SEGMENTOS_DISPONIBLES = 'resumenCompra_segmentosDisponibles'
const STORAGE_KEY_ESTADOS_DISPONIBLES = 'resumenCompra_estadosDisponibles'
const STORAGE_KEY_SOLO_COMPRA = 'resumenCompra_soloCompra'
const STORAGE_KEY_PROTO_SEG = 'compra_prototipos_segundas'
const LOCAL_EVENT_PROTO_SEG = 'compraProtoSegChange'

const getStoredProtoSeg = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROTO_SEG)
    if (!stored) return { prototipos: false, segundas: false }
    const parsed = JSON.parse(stored)
    return {
      prototipos: Boolean(parsed?.prototipos),
      segundas: Boolean(parsed?.segundas),
    }
  } catch {
    return { prototipos: false, segundas: false }
  }
}

const ResumenCompra = ({
  onNavigateToDetalle,
  onNavigateToResumenDetalle,
  escenarioSeleccionado,
  setEscenarioSeleccionado,
  tipoNegocio,
  setTipoNegocio,
  escenarios = [],
  refetchEscenarios,
  esSoloLectura = false,
}) => {
  const [compraData, setCompraData] = useState([])
  const [escenarioComparacion, setEscenarioComparacion] = useState('')
  const [contrasteData, setContrasteData] = useState([])
  const [mostrarContraste, setMostrarContraste] = useState(false)
  const [modalAbierto, setModalAbierto] = useState(false)
  const [segmentosDisponibles, setSegmentosDisponibles] = useState([])
  const [estadosDisponibles, setEstadosDisponibles] = useState([])
  const [segmentosSeleccionados, setSegmentosSeleccionados] = useState([])
  const [estadosSeleccionados, setEstadosSeleccionados] = useState([])
  const [filtrosInicializados, setFiltrosInicializados] = useState(false)
  const [formData, setFormData] = useState({
    nombre: '',
    escenarioBase: 'inicial_2025_6',
    descripcion: '',
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [modalManipulacionAbierto, setModalManipulacionAbierto] = useState(false)
  const [manipulacionData, setManipulacionData] = useState({
    accion: '',
    conjunto: 'Todo',
    modelo: '',
  })
  const [soloCompra, setSoloCompra] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SOLO_COMPRA)
      if (stored === null) return false
      return stored === 'true'
    } catch {
      return false
    }
  })
  const [prototipos, setPrototipos] = useState(() => getStoredProtoSeg().prototipos)
  const [segundas, setSegundas] = useState(() => getStoredProtoSeg().segundas)
  const [puedeCongelar, setPuedeCongelar] = useState(false)

  // Persistir "Solo compra" en localStorage cuando cambie
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SOLO_COMPRA, JSON.stringify(soloCompra))
    } catch {
      // ignorar si localStorage no está disponible
    }
  }, [soloCompra])

  // Persistir y compartir estado de prototipos/segundas entre secciones
  useEffect(() => {
    try {
      const payload = { prototipos, segundas }
      localStorage.setItem(STORAGE_KEY_PROTO_SEG, JSON.stringify(payload))
      window.dispatchEvent(new CustomEvent(LOCAL_EVENT_PROTO_SEG, { detail: payload }))
    } catch {
      // ignorar si localStorage no está disponible
    }
  }, [prototipos, segundas])

  // Sincronizar cambios desde otras secciones/pestañas
  useEffect(() => {
    const handleProtoSegStorage = (e) => {
      if (e.key !== STORAGE_KEY_PROTO_SEG || !e.newValue) return
      try {
        const parsed = JSON.parse(e.newValue)
        setPrototipos(Boolean(parsed?.prototipos))
        setSegundas(Boolean(parsed?.segundas))
      } catch {
        // Ignorar payload inválido
      }
    }

    const handleProtoSegLocalEvent = (e) => {
      const detail = e?.detail || {}
      setPrototipos(Boolean(detail.prototipos))
      setSegundas(Boolean(detail.segundas))
    }

    window.addEventListener('storage', handleProtoSegStorage)
    window.addEventListener(LOCAL_EVENT_PROTO_SEG, handleProtoSegLocalEvent)
    return () => {
      window.removeEventListener('storage', handleProtoSegStorage)
      window.removeEventListener(LOCAL_EVENT_PROTO_SEG, handleProtoSegLocalEvent)
    }
  }, [])

  // Función para cargar datos de resumen de compra (extraída para reutilizar)
  const fetchResumenCompra = useCallback(async () => {
    try {
      const data = await getResumenCompra(escenarioSeleccionado, tipoNegocio, soloCompra, prototipos, segundas)
      if (data && data.compra_general) {
        setCompraData(data.compra_general)
        
        // Extraer segmentos y estados únicos
        const segmentosUnicos = [...new Set(
          data.compra_general
            .map(item => item.segmento)
            .filter(segmento => segmento != null && segmento !== '')
        )].sort()
        
        const estadosUnicos = [...new Set(
          data.compra_general
            .map(item => item.Estado)
            .filter(estado => estado != null && estado !== '')
        )].sort()
        
        setSegmentosDisponibles(segmentosUnicos)
        setEstadosDisponibles(estadosUnicos)
        
        // Guardar opciones disponibles en localStorage para compartir con ResumenCompraDetalle
        localStorage.setItem(STORAGE_KEY_SEGMENTOS_DISPONIBLES, JSON.stringify(segmentosUnicos))
        localStorage.setItem(STORAGE_KEY_ESTADOS_DISPONIBLES, JSON.stringify(estadosUnicos))
        
        // Inicializar filtros SOLO la primera vez
        if (!filtrosInicializados) {
          try {
            const storedSegmentos = localStorage.getItem(STORAGE_KEY_SEGMENTOS)
            const storedEstados = localStorage.getItem(STORAGE_KEY_ESTADOS)
            
            if (storedSegmentos) {
              const segmentosParsed = JSON.parse(storedSegmentos)
              // Validar que los segmentos guardados existan en los disponibles
              const segmentosValidos = segmentosParsed.filter(seg => segmentosUnicos.includes(seg))
              setSegmentosSeleccionados(segmentosValidos)
            } else {
              // Primera vez: seleccionar todos
              setSegmentosSeleccionados(segmentosUnicos)
            }
            
            if (storedEstados) {
              const estadosParsed = JSON.parse(storedEstados)
              // Validar que los estados guardados existan en los disponibles
              const estadosValidos = estadosParsed.filter(est => estadosUnicos.includes(est))
              setEstadosSeleccionados(estadosValidos)
            } else {
              // Primera vez: seleccionar todos
              setEstadosSeleccionados(estadosUnicos)
            }
          } catch (error) {
            console.error('Error al cargar filtros:', error)
            setSegmentosSeleccionados(segmentosUnicos)
            setEstadosSeleccionados(estadosUnicos)
          }
          
          setFiltrosInicializados(true)
        }
      }
    } catch (error) {
      console.error('Error fetching resumen compra:', error)
    }
  }, [escenarioSeleccionado, tipoNegocio, soloCompra, prototipos, segundas, filtrosInicializados])

  // Cargar datos y inicializar filtros
  useEffect(() => {
    fetchResumenCompra()
  }, [fetchResumenCompra])

  // Consultar estado de congelar fecha (puede_congelar) para habilitar/deshabilitar botón
  useEffect(() => {
    const fetchPuedeCongelar = async () => {
      const res = await getCongelarFechaEstado()
      setPuedeCongelar(res.success && res.puedeCongelar === true)
    }
    fetchPuedeCongelar()
  }, [])

  // Escuchar cambios en localStorage para sincronización bidireccional
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY_SEGMENTOS && e.newValue) {
        try {
          const segmentosParsed = JSON.parse(e.newValue)
          if (filtrosInicializados) {
            setSegmentosSeleccionados(Array.isArray(segmentosParsed) ? segmentosParsed : [])
          }
        } catch (error) {
          console.error('Error al parsear segmentos desde storage:', error)
        }
      }
      if (e.key === STORAGE_KEY_ESTADOS && e.newValue) {
        try {
          const estadosParsed = JSON.parse(e.newValue)
          if (filtrosInicializados) {
            setEstadosSeleccionados(Array.isArray(estadosParsed) ? estadosParsed : [])
          }
        } catch (error) {
          console.error('Error al parsear estados desde storage:', error)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    
    // También escuchar cambios locales usando un evento personalizado
    const handleLocalStorageChange = (e) => {
      if (e.detail.key === STORAGE_KEY_SEGMENTOS && filtrosInicializados) {
        setSegmentosSeleccionados(Array.isArray(e.detail.value) ? e.detail.value : [])
      }
      if (e.detail.key === STORAGE_KEY_ESTADOS && filtrosInicializados) {
        setEstadosSeleccionados(Array.isArray(e.detail.value) ? e.detail.value : [])
      }
    }

    window.addEventListener('localStorageChange', handleLocalStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('localStorageChange', handleLocalStorageChange)
    }
  }, [filtrosInicializados])

  // Guardar filtros y modelos cuando cambien (SOLO después de inicializar)
  useEffect(() => {
    if (!filtrosInicializados || compraData.length === 0) return
    
    // Guardar segmentos
    localStorage.setItem(STORAGE_KEY_SEGMENTOS, JSON.stringify(segmentosSeleccionados))
    
    // Calcular y guardar modelos filtrados
    const modelosFiltrados = compraData
      .filter(item => 
        segmentosSeleccionados.includes(item.segmento) &&
        estadosSeleccionados.includes(item.Estado)
      )
      .map(item => ({ modelo_planeacion: item.modelo_planeacion }))
    
    localStorage.setItem(STORAGE_KEY_MODELOS_FILTRADOS, JSON.stringify(modelosFiltrados))
  }, [segmentosSeleccionados, compraData, filtrosInicializados, estadosSeleccionados])

  // Guardar estados cuando cambien (SOLO después de inicializar)
  useEffect(() => {
    if (!filtrosInicializados || compraData.length === 0) return
    
    localStorage.setItem(STORAGE_KEY_ESTADOS, JSON.stringify(estadosSeleccionados))
    
    // Calcular y guardar modelos filtrados
    const modelosFiltrados = compraData
      .filter(item => 
        segmentosSeleccionados.includes(item.segmento) &&
        estadosSeleccionados.includes(item.Estado)
      )
      .map(item => ({ modelo_planeacion: item.modelo_planeacion }))
    
    localStorage.setItem(STORAGE_KEY_MODELOS_FILTRADOS, JSON.stringify(modelosFiltrados))
  }, [estadosSeleccionados, compraData, filtrosInicializados, segmentosSeleccionados])

  const handleEscenarioChange = (event) => {
    setEscenarioSeleccionado(event.target.value)
    setMostrarContraste(false)
  }

  const handleEscenarioComparacionChange = (event) => {
    setEscenarioComparacion(event.target.value)
    setMostrarContraste(false)
  }

  const handleTipoNegocioChange = (event) => {
    setTipoNegocio(event.target.value)
    setMostrarContraste(false)
    // Resetear escenarios de comparación cuando cambia el tipo de negocio
    setEscenarioComparacion('')
  }

  // Función auxiliar para actualizar filtros y notificar cambios
  const actualizarFiltro = (tipo, nuevosValores) => {
    const storageKey = tipo === 'segmentos' ? STORAGE_KEY_SEGMENTOS : STORAGE_KEY_ESTADOS
    const setter = tipo === 'segmentos' ? setSegmentosSeleccionados : setEstadosSeleccionados
    
    setter(nuevosValores)
    localStorage.setItem(storageKey, JSON.stringify(nuevosValores))
    
    // Emitir evento personalizado para sincronización en la misma pestaña
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key: storageKey, value: nuevosValores }
    }))
  }

  const handleSegmentosChange = (event) => {
    const {
      target: { value },
    } = event
    const nuevosValores = typeof value === 'string' ? value.split(',') : value
    actualizarFiltro('segmentos', nuevosValores)
  }

  const handleEstadosChange = (event) => {
    const {
      target: { value },
    } = event
    const nuevosValores = typeof value === 'string' ? value.split(',') : value
    actualizarFiltro('estados', nuevosValores)
  }

  const handleCalcularContraste = async () => {
    if (
      !escenarioSeleccionado ||
      !escenarioComparacion ||
      escenarioSeleccionado === escenarioComparacion
    ) {
      alert(
        'Debe seleccionar dos escenarios diferentes para calcular el contraste',
      )
      return
    }

    try {
      const data = await getContrasteEscenarios(
        escenarioSeleccionado,
        escenarioComparacion,
        tipoNegocio
      )
      if (data && data.escenarios) {
        setContrasteData(data.escenarios)
        setMostrarContraste(true)
      }
    } catch (error) {
      console.error('Error fetching contraste escenarios:', error)
      alert('Error al obtener el contraste de escenarios')
    }
  }

  const handleAbrirModal = () => {
    setModalAbierto(true)
  }

  const handleCerrarModal = () => {
    setModalAbierto(false)
    setFormData({
      nombre: '',
      escenarioBase: 'inicial_2025_6',
      descripcion: '',
    })
  }

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleCrearEscenario = async () => {
    // Validar que todos los campos estén llenos
    if (!formData.nombre || !formData.escenarioBase || !formData.descripcion) {
      alert('Todos los campos son obligatorios')
      return
    }

    try {
      const escenarioData = {
        escenario_base: formData.escenarioBase,
        escenario_nuevo: formData.nombre,
        descripcion: formData.descripcion,
        compania: tipoNegocio,
      }

      const result = await crearEscenarioCompra(escenarioData)
      if (result) {
        // Recargar los escenarios
        await refetchEscenarios()
        handleCerrarModal()
        alert('Escenario creado exitosamente')
      } else {
        alert('Error al crear el escenario')
      }
    } catch (error) {
      console.error('Error creando escenario:', error)
      alert('Error al crear el escenario')
    }
  }

  const handleEliminarEscenario = async () => {
    // Validar que hay un escenario seleccionado y que no sea el inicial
    if (!escenarioSeleccionado || escenarioSeleccionado === 'inicial_2025_6') {
      alert('No se puede eliminar el escenario inicial')
      return
    }

    // Confirmar eliminación
    const confirmacion = window.confirm(
      `¿Está seguro que desea eliminar el escenario "${escenarioSeleccionado}"?`,
    )
    if (!confirmacion) {
      return
    }

    try {
      const result = await eliminarEscenarioCompra(
        escenarioSeleccionado,
        tipoNegocio,
      )
      if (result) {
        // Recargar los escenarios
        await refetchEscenarios()
        // Cambiar al escenario inicial si se eliminó el seleccionado
        setEscenarioSeleccionado('inicial_2025_6')
        alert('Escenario eliminado exitosamente')
      } else {
        alert('Error al eliminar el escenario')
      }
    } catch (error) {
      console.error('Error eliminando escenario:', error)
      alert('Error al eliminar el escenario')
    }
  }

  const handleCompraEnFirme = async () => {
    // Validar que hay un escenario seleccionado
    if (!escenarioSeleccionado) {
      alert('Debe seleccionar un escenario')
      return
    }

    // Confirmar la acción
    const confirmacion = window.confirm(
      `¿Está seguro que desea procesar la compra en firme para el escenario "${escenarioSeleccionado}"?`,
    )
    if (!confirmacion) {
      return
    }

    try {
      console.log( escenarioSeleccionado, tipoNegocio, segmentosSeleccionados)
      const result = await compraEnFirme(escenarioSeleccionado, tipoNegocio, segmentosSeleccionados)
      if (result.success) {
        alert(result.message)
      } else {
        alert(result.message)
      }
    } catch (error) {
      console.error('Error procesando compra en firme:', error)
      alert('Error al procesar la compra en firme')
    }
  }

  const handleCongelarZME2L = async () => {
    const confirmacion = window.confirm('¿Seguro que deseas realizar esta acción?')
    if (!confirmacion) return
    try {
      const result = await congelarFecha()
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' })
        fetchResumenCompra()
        const estado = await getCongelarFechaEstado()
        setPuedeCongelar(estado.success && estado.puedeCongelar === true)
      } else {
        setSnackbar({ open: true, message: result.message || 'Error al congelar', severity: 'error' })
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al congelar fecha', severity: 'error' })
    }
  }

  const handleActualizarLlegadas = async () => {
    // La descripción se genera automáticamente con el formato actualizado_AAAA_MM
    const ahora = new Date()
    const anio = ahora.getFullYear()
    const mes = String(ahora.getMonth() + 1).padStart(2, '0')
    const descripcion = `actualizado_${anio}_${mes}`

    try {
      const result = await actualizarLlegadas(tipoNegocio, descripcion)
      if (result.success) {
        toast.success(result.message || 'Llegadas actualizadas exitosamente')
        // Recargar los escenarios y los datos del resumen
        await refetchEscenarios()
        await fetchResumenCompra()
      } else {
        toast.error(result.message || 'Error al actualizar las llegadas')
      }
    } catch (error) {
      toast.error('Error al actualizar las llegadas')
    }
  }

  // Obtener modelos únicos del escenario actual
  const modelosDisponibles = useMemo(() => {
    if (!compraData || compraData.length === 0) return []
    const modelosUnicos = [...new Set(
      compraData
        .map(item => item.modelo_planeacion)
        .filter(modelo => modelo != null && modelo !== '')
    )].sort()
    return modelosUnicos
  }, [compraData])

  const handleAbrirModalManipulacion = () => {
    setModalManipulacionAbierto(true)
    setManipulacionData({
      accion: '',
      conjunto: 'Todo',
      modelo: '',
    })
  }

  const handleCerrarModalManipulacion = () => {
    setModalManipulacionAbierto(false)
    setManipulacionData({
      accion: '',
      conjunto: 'Todo',
      modelo: '',
    })
  }

  const handleManipulacionChange = (field) => (event) => {
    const { value } = event.target
    setManipulacionData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      }
      // Si se cambia el conjunto a "Todo", limpiar el modelo seleccionado
      if (field === 'conjunto' && value === 'Todo') {
        newData.modelo = ''
      }
      return newData
    })
  }

  const handleAceptarManipulacion = async () => {
    // Validar que la acción esté seleccionada
    if (!manipulacionData.accion) {
      alert('Debe seleccionar una acción')
      return
    }

    // Si el conjunto es "Modelo Específico", validar que se haya seleccionado un modelo
    if (manipulacionData.conjunto === 'Modelo Específico' && !manipulacionData.modelo) {
      alert('Debe seleccionar un modelo')
      return
    }

    // Validar que hay un escenario seleccionado
    if (!escenarioSeleccionado) {
      alert('Debe seleccionar un escenario')
      return
    }

    try {
      if (manipulacionData.conjunto === 'Todo') {
        if (manipulacionData.accion === 'Crear') {
          // Llamar al endpoint para calcular y guardar a nivel de compañía
          const result = await calcularGuardarInventarioCompania(
            tipoNegocio,
            ''
          )
          
          if (result.success) {
            setSnackbar({
              open: true,
              message: result.message || 'Escenario calculado y guardado exitosamente',
              severity: 'success',
            })
            // Recargar los datos
            await fetchResumenCompra()
            handleCerrarModalManipulacion()
          } else {
            setSnackbar({
              open: true,
              message: result.message || 'Error al calcular y guardar el escenario',
              severity: 'error',
            })
          }
        } else if (manipulacionData.accion === 'Eliminar') {
          // Confirmar eliminación
          const confirmacion = window.confirm(
            `¿Está seguro que desea eliminar el escenario "${escenarioSeleccionado}" para toda la compañía?`,
          )
          if (!confirmacion) {
            return
          }

          // Llamar al endpoint para eliminar escenario
          const result = await eliminarEscenarioCompra(
            escenarioSeleccionado,
            tipoNegocio
          )
          
          if (result) {
            setSnackbar({
              open: true,
              message: 'Escenario eliminado exitosamente',
              severity: 'success',
            })
            // Recargar los escenarios
            await refetchEscenarios()
            // Cambiar al escenario inicial si se eliminó el seleccionado
            setEscenarioSeleccionado('inicial_2025_6')
            handleCerrarModalManipulacion()
          } else {
            setSnackbar({
              open: true,
              message: 'Error al eliminar el escenario',
              severity: 'error',
            })
          }
        }
      } else if (manipulacionData.conjunto === 'Modelo Específico') {
        if (manipulacionData.accion === 'Crear') {
          // Llamar al endpoint para calcular y guardar inventario por modelo
          const result = await calcularGuardarInventarioPorModelo(
            manipulacionData.modelo,
            escenarioSeleccionado,
            '', // dias_inventario vacío como en el ejemplo
            ''  // mes_llegada vacío como en el ejemplo
          )
          
          if (result.success) {
            setSnackbar({
              open: true,
              message: result.message || 'Inventario calculado y guardado exitosamente para el modelo',
              severity: 'success',
            })
            // Recargar los datos
            await fetchResumenCompra()
            handleCerrarModalManipulacion()
          } else {
            setSnackbar({
              open: true,
              message: result.message || 'Error al calcular y guardar el inventario para el modelo',
              severity: 'error',
            })
          }
        } else if (manipulacionData.accion === 'Eliminar') {
          // Confirmar eliminación
          const confirmacion = window.confirm(
            `¿Está seguro que desea eliminar el escenario "${escenarioSeleccionado}" para el modelo "${manipulacionData.modelo}"?`,
          )
          if (!confirmacion) {
            return
          }

          // Llamar al endpoint para eliminar escenario por modelo
          const result = await eliminarEscenarioPorModelo(
            escenarioSeleccionado,
            tipoNegocio,
            manipulacionData.modelo
          )
          
          if (result.success) {
            setSnackbar({
              open: true,
              message: result.message || 'Escenario eliminado exitosamente para el modelo',
              severity: 'success',
            })
            // Recargar los datos
            await fetchResumenCompra()
            handleCerrarModalManipulacion()
          } else {
            setSnackbar({
              open: true,
              message: result.message || 'Error al eliminar el escenario para el modelo',
              severity: 'error',
            })
          }
        }
      }
    } catch (error) {
      console.error('Error en manipulación de escenario:', error)
      setSnackbar({
        open: true,
        message: 'Error al procesar la solicitud',
        severity: 'error',
      })
    }
  }

  // Obtener los nombres de los meses dinámicamente
  const { mesAnterior, mesActual } = getMesesEspanol()

  // Filtrar datos por segmentos y estados seleccionados
  const compraDataFiltrada = compraData.filter(item => 
    (segmentosSeleccionados.length > 0 && 
    segmentosSeleccionados.includes(item.segmento)) &&
    (estadosSeleccionados.length > 0 && 
    estadosSeleccionados.includes(item.Estado))
  )

  // Calcular totales dinámicamente basados en los datos filtrados
  const totalesDinamicos = useMemo(() => 
    compraDataFiltrada.reduce((acc, item) => ({
      total_avg_sell_in: acc.total_avg_sell_in + (parseFloat(item.avg_sell_in) || 0),
      total_avg_sell_out: acc.total_avg_sell_out + (parseFloat(item.avg_sell_out) || 0),
      total_sell_in_mes_pasado: acc.total_sell_in_mes_pasado + (parseFloat(item.sell_in_mes_pasado) || 0),
      total_sell_out_mes_pasado: acc.total_sell_out_mes_pasado + (parseFloat(item.sell_out_mes_pasado) || 0),
      total_sell_in_mes_actual: acc.total_sell_in_mes_actual + (parseFloat(item.sell_in_mes_actual) || 0),
      total_sell_in_fecha_facturacion: acc.total_sell_in_fecha_facturacion + (parseFloat(item.sell_in_fecha_facturacion) || 0),
      total_llegadas_unidades: acc.total_llegadas_unidades + (parseFloat(item.llegadas_unidades) || 0),
      total_llegadas_unidades_necesarias: acc.total_llegadas_unidades_necesarias + (parseFloat(item.llegadas_unidades_necesarias) || 0),
      total_llegadas_unidades_necesarias_plus: acc.total_llegadas_unidades_necesarias_plus + (parseFloat(item.llegadas_unidades_necesarias_plus) || 0),
      total_llegadas_unidades_necesarias_plus_2: acc.total_llegadas_unidades_necesarias_plus_2 + (parseFloat(item.llegadas_unidades_necesarias_plus_2) || 0),
      total_compra_usd: acc.total_compra_usd + (parseFloat(item.compra_usd) || 0),
      total_compra_usd_plus: acc.total_compra_usd_plus + (parseFloat(item.compra_usd_plus) || 0),
      total_compra_usd_plus_2: acc.total_compra_usd_plus_2 + (parseFloat(item.compra_usd_plus_2) || 0),
      total_dias_inventario: (formatNumber(acc.total_dias_inventario, 10)) + (parseFloat(item.Dias_Inventario) || 0),
      total_inventario_final_dias_pv: acc.total_inventario_final_dias_pv + (parseFloat(item.inventario_final_dias_pv) || 0),
      total_inventario_final_un_pv_teorico: acc.total_inventario_final_un_pv_teorico + (parseFloat(item.inventario_final_un_pv_teorico) || 0),
    }), {
      total_avg_sell_in: 0,
      total_avg_sell_out: 0,
      total_sell_in_mes_pasado: 0,
      total_sell_out_mes_pasado: 0,
      total_sell_in_mes_actual: 0,
      total_sell_in_fecha_facturacion: 0,
      total_llegadas_unidades: 0,
      total_llegadas_unidades_necesarias: 0,
      total_llegadas_unidades_necesarias_plus: 0,
      total_llegadas_unidades_necesarias_plus_2: 0,
      total_compra_usd: 0,
      total_compra_usd_plus: 0,
      total_compra_usd_plus_2: 0,
      total_dias_inventario: 0,
      total_inventario_final_dias_pv: 0,
      total_inventario_final_un_pv_teorico: 0,
    })
  , [compraDataFiltrada])

  // Datos con fila de totales siempre en la primera posición
  const compraDataConTotales = useMemo(() => {
    // Crear objeto de fila de totales
    const filaTotales = {
      marca: 'TOTAL',
      modelo_planeacion: '',
      fecha_disponible_para_facturacion: '',
      bias: '',
      congelado: '',
      avg_sell_in: totalesDinamicos.total_avg_sell_in,
      avg_sell_out: totalesDinamicos.total_avg_sell_out,
      sell_in_mes_pasado: totalesDinamicos.total_sell_in_mes_pasado,
      sell_out_mes_pasado: totalesDinamicos.total_sell_out_mes_pasado,
      sell_in_mes_actual: totalesDinamicos.total_sell_in_mes_actual,
      sell_in_fecha_facturacion: totalesDinamicos.total_sell_in_fecha_facturacion,
      llegadas_unidades: totalesDinamicos.total_llegadas_unidades,
      llegadas_unidades_necesarias: totalesDinamicos.total_llegadas_unidades_necesarias,
      llegadas_unidades_necesarias_plus: totalesDinamicos.total_llegadas_unidades_necesarias_plus,
      llegadas_unidades_necesarias_plus_2: totalesDinamicos.total_llegadas_unidades_necesarias_plus_2,
      fob: '',
      compra_usd: totalesDinamicos.total_compra_usd,
      compra_usd_plus: totalesDinamicos.total_compra_usd_plus,
      compra_usd_plus_2: totalesDinamicos.total_compra_usd_plus_2,
      Dias_Inventario: totalesDinamicos.total_dias_inventario,
      inventario_final_dias_pv: totalesDinamicos.total_inventario_final_dias_pv,
      inventario_final_un_pv_teorico: totalesDinamicos.total_inventario_final_un_pv_teorico,
      detalle: '',
      isTotal: true // Marcador para identificar la fila de totales
    }
    
    // Siempre agregar la fila de totales al inicio
    return [filaTotales, ...compraDataFiltrada]
  }, [compraDataFiltrada, totalesDinamicos])

  const columns = [
    {
      accessorKey: 'marca',
      header: <div ><p style={{ fontSize: '10px' }}>Marca</p></div>,
      size: 100,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#f5f5f5 !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
        },
      },
    },
    {
      accessorKey: 'modelo_planeacion',
      header: <div ><p style={{ fontSize: '10px' }}>Modelo</p></div>,
      size: 130,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#f5f5f5 !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
        },
      },
    },
    {
      accessorKey: 'fecha_disponible_para_facturacion',
      header: <div ><p style={{ fontSize: '10px' }}>Mes</p> <p style={{ fontSize: '10px' }}>de</p> <p style={{ fontSize: '10px' }}>facturación</p></div>,
      size: 120,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#f5f5f5 !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
        },
      },
    },
    {
      accessorKey: 'bias',
      header: <div ><p style={{ fontSize: '10px' }}>BIAS</p></div>,
      size: 90,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#b8e6b8 !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
        },
      },
      Cell: ({ cell }) => {
        const value = cell.getValue()
        return value !== '' ? `${formatNumber(parseFloat(value) * 100,10)}%` : ''
      },
    },
    {
      accessorKey: 'congelado',
      header: <div ><p style={{ fontSize: '10px' }}>Compra</p><p style={{ fontSize: '10px' }}>en</p><p style={{ fontSize: '10px' }}>firme</p></div>,
      size: 100,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: 'white !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
        },
      }
    },
    {
      header: 'Demanda',
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#26aaf4 !important',
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
        },
      },
      columns: [
        {
          accessorKey: 'avg_sell_in',
          header: <div ><p style={{ fontSize: '10px' }}>Promedio</p><p style={{ fontSize: '10px' }}>3 meses</p><p style={{ fontSize: '10px' }}>Sell In</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d0edff !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return value !== '' && value != null ? formatNumber(value,10) : ''
          },
        },
        {
          accessorKey: 'avg_sell_out',
          header: <div ><p style={{ fontSize: '10px' }}>Promedio</p><p style={{ fontSize: '10px' }}>3 meses</p><p style={{ fontSize: '10px' }}>Sell Out</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d0edff !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return value !== '' && value != null ? formatNumber(value,10) : ''
          },
        },
        {
          accessorKey: 'sell_in_mes_pasado',
          header: <div ><p style={{ fontSize: '10px' }}>Sell In</p> <p style={{ fontSize: '10px' }}>{mesAnterior}</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d0edff !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return value !== '' && value != null ? formatNumber(value,10) : ''
          },
        },
        {
          accessorKey: 'sell_out_mes_pasado',
          header: <div ><p style={{ fontSize: '10px' }}>Sell Out</p> <p style={{ fontSize: '10px' }}>{mesAnterior}</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d0edff !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return value !== '' && value != null ? formatNumber(value,10) : ''
          },
        },
        {
          accessorKey: 'sell_in_mes_actual',
          header: <div ><p style={{ fontSize: '10px' }}>Sell In</p> <p style={{ fontSize: '10px' }}>{mesActual}</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d0edff !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return value !== '' && value != null ? formatNumber(value,10) : ''
          },
        },
        {
          accessorKey: 'sell_in_fecha_facturacion',
          header: <div ><p style={{ fontSize: '10px' }}>Sell In</p> <p style={{ fontSize: '10px' }}>Mes Compra</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d0edff !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return value !== '' && value != null ? formatNumber(value,10) : ''
          },
        },
      ],
    },
    {
      header: 'Compra',
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#b19cd9 !important',
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
        },
      },
      columns: [
        {
          accessorKey: 'llegadas_unidades',
          header: <div ><p style={{ fontSize: '10px' }}>Llegadas</p> <p style={{ fontSize: '10px' }}>en</p> <p style={{ fontSize: '10px' }}>firme</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() ? `${formatNumber(cell.getValue(),10)}` : 0,
        },
        {
          accessorKey: 'llegadas_unidades_necesarias',
          header: <div ><p style={{ fontSize: '10px' }}>Compra</p> <p style={{ fontSize: '10px' }}>{mesActual}</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() ? `${formatNumber(cell.getValue())}` : 0,
        },
        {
          accessorKey: 'llegadas_unidades_necesarias_plus',
          header: <div ><p style={{ fontSize: '10px' }}>Compra</p><p style={{ fontSize: '10px' }}>{mesActual} T+1</p></div>,
          size: 120,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() != null && cell.getValue() !== '' ? formatNumber(cell.getValue(), 10) : '0',
        },
        {
          accessorKey: 'llegadas_unidades_necesarias_plus_2',
          header: <div ><p style={{ fontSize: '10px' }}>Compra</p><p style={{ fontSize: '10px' }}>{mesActual} T+2</p></div>,
          size: 120,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() != null && cell.getValue() !== '' ? formatNumber(cell.getValue(), 10) : '0',
        },
        {
          accessorKey: 'fob',
          header: <div ><p style={{ fontSize: '10px' }}>FOB</p></div>,
          size: 120,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell, row }) => {
            // No permitir edición en la fila de totales ni en escenarios de solo lectura
            if (row.original.isTotal || esSoloLectura) {
              return cell.getValue() ? `$${formatNumber(cell.getValue())}` : '-'
            }

            const handleValueChange = async (value) => {
              const resultado = await actualizarFOB(row.original.modelo_planeacion, value)
              if (resultado.success) {
                setSnackbar({
                  open: true,
                  message: 'FOB editado con éxito',
                  severity: 'success',
                })
                // Recargar los datos de la tabla manteniendo los filtros actuales
                await fetchResumenCompra()
              } else {
                setSnackbar({
                  open: true,
                  message: resultado.message || 'Error al editar el FOB',
                  severity: 'error',
                })
              }
            }

            return (
              <TextField
                type="number"
                key={`${Math.random()+cell.getValue()}fob`}
                defaultValue={row.original.fob || 0}
                onBlur={(e) => {
                  handleValueChange(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleValueChange(e.target.value)
                    e.target.blur() // Quita el foco del input
                  }
                }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        '& .MuiTypography-root, &': {
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        },
                        mr: 0,
                      }}
                    >
                      $
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputAdornment-root': {
                    color: '#666',
                    marginRight: '0 !important',
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 8px 4px 0',
                    fontSize: '12px',
                    textAlign: 'left',
                  },
                }}
              />
            )
          },
        },
        {
          accessorKey: 'compra_usd',
          header: <div ><p style={{ fontSize: '10px' }}>Compra</p><p style={{ fontSize: '10px' }}>{mesActual}</p><p style={{ fontSize: '10px' }}>(USD)</p></div>,
          size: 140,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() ? `$${formatNumber(cell.getValue())}` : 0,
        },
       
        {
          accessorKey: 'compra_usd_plus',
          header: <div ><p style={{ fontSize: '10px' }}>Compra</p><p style={{ fontSize: '10px' }}>T+1 (USD)</p></div>,
          size: 130,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() != null && cell.getValue() !== '' ? `$${formatNumber(cell.getValue())}` : '0',
        },
        {
          accessorKey: 'compra_usd_plus_2',
          header: <div ><p style={{ fontSize: '10px' }}>Compra</p><p style={{ fontSize: '10px' }}>T+2 (USD)</p></div>,
          size: 130,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#e4d6ef !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => cell.getValue() != null && cell.getValue() !== '' ? `$${formatNumber(cell.getValue())}` : '0',
        },
      ],
    },
    {
      header: <div ><p style={{ fontSize: '12px' }}>Inventarios</p></div>,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#90c695 !important',
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
        },
      },
      columns: [
        {
          accessorKey: 'Dias_Inventario',
          header: <div ><p style={{ fontSize: '10px' }}>Inv Obj</p> <p style={{ fontSize: '10px' }}>Días</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d4edda !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue(),10),
        },
        {
          accessorKey: 'inventario_final_dias_pv',
          header: <div ><p style={{ fontSize: '10px' }}>Inv Proy</p><p style={{ fontSize: '10px' }}>Final Comp</p><p style={{ fontSize: '10px' }}>Días</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d4edda !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue(),10),
        },
        {
          accessorKey: 'inventario_final_un_pv_teorico',
          header: <div ><p style={{ fontSize: '10px' }}>Inv Proy</p><p style={{ fontSize: '10px' }}>Final Comp</p><p style={{ fontSize: '10px' }}>UND</p></div>,
          size: 100,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#d4edda !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue(),10),
        },
      ],
    },
    {
      accessorKey: 'detalle',
      header: <div ><p style={{ fontSize: '10px' }}>Detalle</p></div>,
      size: 100,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#f5f5f5 !important',
          color: 'black',
          fontWeight: 'bold',
        },
      },
      Cell: ({ row }) => {
        // No mostrar el botón en la fila de totales
        if (row.original.isTotal) {
          return null
        }
        return (
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold',
              minWidth: '60px',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
            onClick={() => {
              if (onNavigateToDetalle) {
                // Obtener los modelos únicos de la tabla filtrada
                const modelosFiltrados = compraDataFiltrada.map(item => ({
                  modelo_planeacion: item.modelo_planeacion
                }))
                onNavigateToDetalle(tipoNegocio, row.original.modelo_planeacion, modelosFiltrados)
              }
            }}
          >
            Ver
          </Button>
        )
      },
    },
  ]

  // Columnas para la tabla de contraste
  const contrasteColumns = [
    {
      accessorKey: 'modelo_planeacion',
      header: 'Modelo',
      size: 150,
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#f5f5f5 !important',
          color: 'black',
          fontWeight: 'bold',
          borderRight: '1px solid #ddd',
        },
      },
    },
    {
      header: 'Escenario 1',
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#ff9800 !important',
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
        },
      },
      columns: [
        {
          accessorKey: 'nivel_servicio_A',
          header: 'Nivel de servicio',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#ffe0b3 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => `${parseFloat((cell.getValue())) * 100}%`,
        },
        {
          accessorKey: 'dias_inventario_A',
          header: 'Días de inventario',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#ffe0b3 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
        },
        {
          accessorKey: 'mes_llegada_A',
          header: 'mes_llegada',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#ffe0b3 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
        },
        {
          accessorKey: 'plan_ventas_lt_A',
          header: 'Plan de Ventas del LT',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#ffe0b3 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue()),
        },
        {
          accessorKey: 'compra_propuesta_A',
          header: 'Compra Propuesta',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#ffe0b3 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue()),
        },
        {
          accessorKey: 'dias_inventario_final_A',
          header: 'Días de inventario al final de la compra',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#ffe0b3 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue()),
        },
      ],
    },
    {
      header: 'Escenario 2',
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: '#4caf50 !important',
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
        },
      },
      columns: [
        {
          accessorKey: 'nivel_servicio_B',
          header: 'Nivel de servicio',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#c8e6c9 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
       Cell: ({ cell }) => `${parseFloat((cell.getValue())) * 100}%`,
        },
        {
          accessorKey: 'dias_inventario_B',
          header: 'Días de inventario',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#c8e6c9 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
        },
        {
          accessorKey: 'mes_llegada_B',
          header: 'mes_llegada',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#c8e6c9 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
        },
        {
          accessorKey: 'plan_ventas_lt_B',
          header: 'Plan de Ventas del LT',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#c8e6c9 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue()),
        },
        {
          accessorKey: 'compra_propuesta_B',
          header: 'Compra Propuesta',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#c8e6c9 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue()),
        },
        {
          accessorKey: 'dias_inventario_final_B',
          header: 'Días de inventario al final de la compra',
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: '#c8e6c9 !important',
              color: 'black',
              fontSize: '12px !important',
            },
          },
          Cell: ({ cell }) => formatNumber(cell.getValue()),
        },
      ],
    },
  ]

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5 }}>
        {/* Selector de escenario */}
        <Box
          sx={{
            marginBottom: "15px",
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }}>
            <InputLabel>Escenario</InputLabel>
            <Select
              value={escenarioSeleccionado}
              onChange={handleEscenarioChange}
              label="Escenario"
            >
              {escenarios && escenarios.length > 0 ? (
                escenarios.map((escenario) => (
                  <MenuItem
                    key={escenario.escenario}
                    value={escenario.escenario}
                  >
                    {escenario.escenario}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No hay escenarios disponibles</MenuItem>
              )}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleAbrirModal}
            size="small"
            disabled={esSoloLectura}
            sx={{
              backgroundColor: '#b8e6b8',
              color: 'black',
              '&:hover': {
                backgroundColor: '#a0d0a0',
              },
              '&:disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999',
              },
            }}
          >
            Crear escenario
          </Button>
          <Button
            variant="contained"
            onClick={handleEliminarEscenario}
            size="small"
            disabled={
              !escenarioSeleccionado ||
              escenarioSeleccionado === 'inicial_2025_6' ||
              esSoloLectura
            }
            sx={{
              backgroundColor: '#ffcccb',
              color: 'black',
              '&:hover': {
                backgroundColor: '#ffb3b3',
              },
              '&:disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999',
              },
            }}
          >
            Eliminar escenario
          </Button>
          <Button
            variant="contained"
            onClick={handleAbrirModalManipulacion}
            size="small"
            disabled={esSoloLectura}
            sx={{
              backgroundColor: '#9fa8da',
              color: 'white',
              '&:hover': {
                backgroundColor: '#7986cb',
              },
              '&:disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999',
              },
            }}
          >
            Manipulación escenarios
          </Button>
          <Button
            variant="contained"
            onClick={handleActualizarLlegadas}
            size="small"
            disabled={esSoloLectura}
            sx={{
              backgroundColor: '#ffe0b2',
              color: 'black',
              '&:hover': {
                backgroundColor: '#ffcc80',
              },
              '&:disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999',
              },
            }}
          >
            Actualizar llegadas
          </Button>
          {esSoloLectura && (
            <Chip
              label="Solo lectura"
              color="warning"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Selector de compañía */}
        <Box
          sx={{
            marginBottom: 1.5,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }}>
            <InputLabel>Compañía</InputLabel>
            <Select
              value={tipoNegocio}
              onChange={handleTipoNegocioChange}
              label="Compañía"
            >
              <MenuItem value="TVS">TVS</MenuItem>
              <MenuItem value="VICTORY+">VICTORY+</MenuItem>
              <MenuItem value="ELECTRIC">ELECTRIC</MenuItem>
              <MenuItem value="BLUE">BLUE</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={soloCompra}
                onChange={(e) => setSoloCompra(e.target.checked)}
                color="primary"
              />
            }
          label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                Solo compra
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={prototipos}
                onChange={(e) => setPrototipos(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                Prototipos
              </Typography>
            }
            sx={{ ml: 0.5, mr: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={segundas}
                onChange={(e) => setSegundas(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                Segundas
              </Typography>
            }
            sx={{ ml: 0.5, mr: 0 }}
          />
        </Box>

        {/* Selector de segmentos */}
        {segmentosDisponibles.length > 0 && (
          <Box
            sx={{
              marginBottom: 1.5,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl size="small" variant="outlined" sx={{ minWidth: 260 }}>
              <InputLabel>Segmentos</InputLabel>
              <Select
                multiple
                value={segmentosSeleccionados}
                onChange={handleSegmentosChange}
                input={<OutlinedInput label="Segmentos" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {segmentosDisponibles.map((segmento) => (
                  <MenuItem key={segmento} value={segmento}>
                    <Checkbox checked={segmentosSeleccionados.indexOf(segmento) > -1} />
                    <ListItemText primary={segmento} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {segmentosSeleccionados.length} / {segmentosDisponibles.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => actualizarFiltro('segmentos', segmentosDisponibles)}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '13px',
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                  },
                }}
              >
                Todos
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                |
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => actualizarFiltro('segmentos', [])}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '13px',
                  color: '#757575',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Limpiar
              </Button>
            </Box>
          </Box>
        )}

        {/* Selector de estados */}
        {estadosDisponibles.length > 0 && (
          <Box
            sx={{
              marginBottom: 1.5,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl size="small" variant="outlined" sx={{ minWidth: 260 }}>
              <InputLabel>Estados</InputLabel>
              <Select
                multiple
                value={estadosSeleccionados}
                onChange={handleEstadosChange}
                input={<OutlinedInput label="Estados" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {estadosDisponibles.map((estado) => (
                  <MenuItem key={estado} value={estado}>
                    <Checkbox checked={estadosSeleccionados.indexOf(estado) > -1} />
                    <ListItemText primary={estado} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {estadosSeleccionados.length} / {estadosDisponibles.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => actualizarFiltro('estados', estadosDisponibles)}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '13px',
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                  },
                }}
              >
                Todos
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                |
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => actualizarFiltro('estados', [])}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '13px',
                  color: '#757575',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Limpiar
              </Button>
            </Box>
          </Box>
        )}

        {/* Dashboard de métricas */}
        {/* Tabla principal */}
        <MaterialReactTable
          columns={columns}
          data={compraDataConTotales}
          enablePagination
          enableSorting={false}
          enableDensityToggle={false}
          enableColumnResizing
          enableStickyHeader
          enableHiding
          enableColumnActions={false}
          muiTableContainerProps={{
            sx: {
              maxHeight: '600px',
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              '&:hover': {
                backgroundColor: '#f5f5f5 !important',
              },
              ...(row.original.isTotal && {
                backgroundColor: '#fff3cd !important',
                fontWeight: 'bold',
                position: 'sticky',
                top: '95px', // Justo debajo del header sticky (ajustado al nuevo alto compacto)
                zIndex: 9, // Menor que el header pero mayor que las filas normales
                '&:hover': {
                  backgroundColor: '#ffe69c !important',
                },
                '& td': {
                  fontWeight: 'bold',
                  fontSize: '14px',
                },
              }),
            },
          })}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 0,
              border: '1px solid #ddd',
              '& .MuiTableCell-head p': {
                margin: 0,
                lineHeight: 1.2,
              },
            },
          }}
          initialState={{
            pagination: { pageSize: 50, pageIndex: 0 },
            columnPinning: {
              left: ['modelo_planeacion']
            },
            columnVisibility: {
              marca: false,
              fecha_disponible_para_facturacion: false,
              bias: false,
              congelado: false,
              llegadas_unidades_necesarias_plus: false,
              llegadas_unidades_necesarias_plus_2: false,
              compra_usd_plus: false,
              compra_usd_plus_2: false,
            },
          }}
        />

        {/* Botones Compra en Firme y Congelar ZME2L */}
        <Box
          sx={{
            marginLeft: 'auto',
            marginTop: 2,
            marginBottom: 1,
            textAlign: 'center',
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={handleCompraEnFirme}
            disabled={!escenarioSeleccionado || esSoloLectura}
            sx={{
              borderColor: '#4caa64',
              color: '#4caa64',
              '&:hover': {
                backgroundColor: '#e8f5e8',
                borderColor: '#4caa64',
              },
            }}
          >
            Compra en firme
          </Button>
          <Button
            variant="outlined"
            onClick={handleCongelarZME2L}
            disabled={!puedeCongelar || esSoloLectura}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1976d2',
              },
            }}
          >
            🧊  Congelar ZME2L
          </Button>
        </Box>

        {/* Sección completa de contraste de escenarios */}
        <Box
          sx={{
            marginTop: 3,
            padding: 1.5,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '1px solid #ddd',
          }}
        >
          {/* <Typography variant="h4" sx={{ marginBottom: 3, fontWeight: 'bold', color: '#1976d2' }}>
            Contraste de Escenarios
          </Typography>
           */}
          {/* Controles para seleccionar escenarios a comparar */}
          <Box
            sx={{
              marginBottom: 3,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Comparar:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                backgroundColor: '#ff9800',
                color: 'white',
                padding: '8px 16px',
                borderRadius: 1,
                fontWeight: 'bold',
              }}
            >
              {escenarioSeleccionado || 'Ninguno'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              vs
            </Typography>
            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel>Escenario para comparar</InputLabel>
              <Select
                value={escenarioComparacion}
                onChange={handleEscenarioComparacionChange}
                label="Escenario para comparar"
              >
                {escenarios
                  .filter(
                    (escenario) =>
                      escenario.escenario &&
                      escenario.escenario !== escenarioSeleccionado,
                  )
                  .map((escenario) => (
                    <MenuItem
                      key={escenario.escenario}
                      value={escenario.escenario}
                    >
                      {escenario.escenario}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleCalcularContraste}
              disabled={
                !escenarioSeleccionado ||
                !escenarioComparacion ||
                escenarioSeleccionado === escenarioComparacion
              }
              sx={{
                backgroundColor: '#2196f3',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                padding: '12px 24px',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
                '&:disabled': {
                  backgroundColor: '#f5f5f5',
                  color: '#999',
                },
              }}
            >
              Calcular contraste
            </Button>
          </Box>

          {/* Tabla de contraste */}
          {mostrarContraste && (
            <Box sx={{ marginTop: 3 }}>
              <Typography
                variant="h5"
                sx={{ marginBottom: 2, fontWeight: 'bold' }}
              >
                Resultados del Contraste
              </Typography>
              <MaterialReactTable
                columns={contrasteColumns}
                data={contrasteData}
                enablePagination
                enableSorting
                enableDensityToggle={false}
                enableColumnResizing
                enableStickyHeader
                muiTableContainerProps={{
                  sx: {
                    maxHeight: '600px',
                  },
                }}
                muiTableBodyRowProps={{
                  sx: {
                    '&:hover': {
                      backgroundColor: '#f5f5f5 !important',
                    },
                  },
                }}
                muiTablePaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: 0,
                    border: '1px solid #ddd',
                  },
                }}
                initialState={{
                  pagination: { pageSize: 50, pageIndex: 0 },
                }}
              />
            </Box>
          )}

          {/* Mensaje cuando no hay contraste calculado */}
          {!mostrarContraste && (
            <Box
              sx={{
                marginTop: 2,
                padding: 3,
                textAlign: 'center',
                backgroundColor: '#e3f2fd',
                borderRadius: 1,
                border: '1px solid #bbdefb',
              }}
            >
              <Typography variant="body1" sx={{ color: '#1976d2' }}>
                Selecciona dos escenarios diferentes y haz clic en Calcular
                contraste para ver la comparación
              </Typography>
            </Box>
          )}
        </Box>

        {/* Modal para crear escenario */}
        <Dialog
          open={modalAbierto}
          onClose={handleCerrarModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Crear Escenario</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}
            >
              <TextField
                label="Nombre"
                placeholder="NIVEL DE SERVICIO"
                value={formData.nombre}
                onChange={handleInputChange('nombre')}
                required
                fullWidth
              />

              <FormControl fullWidth required>
                <InputLabel>Escenario base</InputLabel>
                <Select
                  value={formData.escenarioBase}
                  onChange={handleInputChange('escenarioBase')}
                  label="Escenario base"
                >
                  {escenarios && escenarios.length > 0 ? (
                    escenarios.map((escenario) => (
                      <MenuItem
                        key={escenario.escenario}
                        value={escenario.escenario}
                      >
                        {escenario.escenario}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No hay escenarios disponibles</MenuItem>
                  )}
                </Select>
              </FormControl>

              <TextField
                label="Descripción"
                placeholder="Diferentes niveles de servicio por modelo"
                value={formData.descripcion}
                onChange={handleInputChange('descripcion')}
                required
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCerrarModal} variant="outlined">
              Cancelar
            </Button>
            <Button
              onClick={handleCrearEscenario}
              variant="contained"
              sx={{
                backgroundColor: '#b8e6b8',
                color: 'black',
                '&:hover': {
                  backgroundColor: '#a0d0a0',
                },
              }}
            >
              CREAR
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Manipulación de Escenarios */}
        <Dialog
          open={modalManipulacionAbierto}
          onClose={handleCerrarModalManipulacion}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            MANIPULACIÓN ESCENARIOS
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}
            >
              <FormControl fullWidth required>
                <InputLabel>Acción</InputLabel>
                <Select
                  value={manipulacionData.accion}
                  onChange={handleManipulacionChange('accion')}
                  label="Acción"
                >
                  <MenuItem value="Crear">Crear</MenuItem>
                  <MenuItem value="Eliminar">Eliminar</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Conjunto</InputLabel>
                <Select
                  value={manipulacionData.conjunto}
                  onChange={handleManipulacionChange('conjunto')}
                  label="Conjunto"
                >
                  <MenuItem value="Todo">Todo</MenuItem>
                  <MenuItem value="Modelo Específico">Modelo Específico</MenuItem>
                </Select>
              </FormControl>

              <FormControl 
                fullWidth 
                required={manipulacionData.conjunto === 'Modelo Específico'}
                disabled={manipulacionData.conjunto !== 'Modelo Específico'}
              >
                <InputLabel>Modelo</InputLabel>
                <Select
                  value={manipulacionData.modelo}
                  onChange={handleManipulacionChange('modelo')}
                  label="Modelo"
                >
                  {modelosDisponibles.length > 0 ? (
                    modelosDisponibles.map((modelo) => (
                      <MenuItem key={modelo} value={modelo}>
                        {modelo}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No hay modelos disponibles</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
            <Button 
              onClick={handleCerrarModalManipulacion} 
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAceptarManipulacion}
              variant="contained"
              sx={{
                backgroundColor: '#9fa8da',
                color: 'white',
                minWidth: 120,
                '&:hover': {
                  backgroundColor: '#7986cb',
                },
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Paper>
  )
}

export default ResumenCompra
